import { useEffect, useRef, useState, type ReactNode } from "react";
import type {
  BuyerDealStructureDto,
  DealStructureCashFlowType,
  DealStructurePriceBasis,
  UpdateBuyerDealStructurePayload,
} from "@/api/buyers";
import { Modal } from "@/components/ui/Modal";
import { Button, ResponsiveGrid, StatusStepper, SubTable, useToast } from "@/design-system/components/ui";
import { formatCurrency, formatNumber } from "@/design-system/lib/format";
import {
  useBuyerDealStructure,
  usePatchBuyerDealStructure,
} from "@/hooks/buyers/useBuyerDealStructure";
import { useDateFormatter } from "@/utils/datetime";
import {
  CONFIRM_STEP_INDEX,
  PRICE_BASIS_LABELS,
  STRUCTURE_CARD,
  W0,
  W1,
  W2,
  W3,
  W4,
  W5,
  WIZARD_STEPS,
  WIZARD_TITLE,
  downPaymentPrefillLine,
  listingCashFlowLine,
  moneyText,
  multipleOf,
  parseMoneyText,
  percentOf,
} from "./dealStructureCopy";
import {
  CashFlowFields,
  DownPaymentField,
  NoteField,
  PriceBasisCards,
  cashFlowReady,
  priceReady,
} from "./DealStructureFields";

/**
 * BUILD THE DEAL STRUCTURE — six steps (W0–W5), one question each.
 *
 * <p>🔴 <b>Phoenix's illustration, not the buyer's.</b> Nothing here asks staff
 * to call the buyer and nothing calls a model: every step is prefilled from the
 * deterministic Suggested structure, and every figure the Review step shows is
 * the server's, computed over what was saved.</p>
 *
 * <p>🔴 <b>Each step saves before it advances.</b> A step's PATCH carries its
 * own fields plus `step` (the resume point). A refused figure comes back as a
 * 400 whose message is the sentence to show; it is shown inline and the
 * wizard stays on the step. SAVE DEAL STRUCTURE (W5) sends `confirm: true`,
 * which marks the structure complete; document 3 is then generated from the
 * package card, never from here.</p>
 *
 * <p>A modal layer, as the indication wizard before it was: the tab renders
 * inside the ~420px record drawer as well as on the full page.</p>
 */
export function DealStructureWizard({
  submissionId,
  open,
  onClose,
}: {
  submissionId: string;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const { date } = useDateFormatter();
  const query = useBuyerDealStructure(submissionId, open);
  const patch = usePatchBuyerDealStructure(submissionId);
  const dto = query.data;

  const [step, setStep] = useState(0);
  const [type, setType] = useState<DealStructureCashFlowType | null>(null);
  const [amountText, setAmountText] = useState("");
  const [estimateText, setEstimateText] = useState("");
  const [downText, setDownText] = useState("");
  const [basis, setBasis] = useState<DealStructurePriceBasis | null>(null);
  const [customText, setCustomText] = useState("");
  const [noteText, setNoteText] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [goBackOpen, setGoBackOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /* 🔴 SEEDED ONCE PER OPEN. Every save invalidates the structure, so the
     query hands back a new object after each step — an effect that re-seeded
     on it would throw away what staff typed on the next step. */
  const seeded = useRef(false);
  useEffect(() => {
    if (!open) {
      seeded.current = false;
      return;
    }
    if (seeded.current || !dto) return;
    seeded.current = true;
    const s = dto.suggested;
    setStep(clampStep(dto.step ?? 0));
    // Ruling Q2: no type is preselected unless one was saved or the listing
    // came from Research with a recorded type.
    setType(dto.cashFlowType ?? s.listingCashFlowType ?? null);
    setAmountText(moneyText(dto.cashFlow ?? s.listingCashFlow));
    setEstimateText(moneyText(dto.freeCashFlowEstimate));
    setDownText(moneyText(dto.downPayment ?? s.downPayment));
    setBasis(dto.priceBasis ?? (canUseAsking(dto) ? "Asking" : null));
    setCustomText(moneyText(dto.customPrice));
    setNoteText(dto.note ?? "");
    setConfirmed(false);
    setGoBackOpen(false);
    setError(null);
  }, [open, dto]);

  const saving = patch.isPending;

  async function save(payload: UpdateBuyerDealStructurePayload, then: number | "done") {
    setError(null);
    try {
      await patch.mutateAsync(payload);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save that step.");
      return;
    }
    if (then === "done") {
      toast.success(W5.saved);
      onClose();
      return;
    }
    setGoBackOpen(false);
    setStep(then);
  }

  function goTo(next: number) {
    setError(null);
    setGoBackOpen(false);
    setStep(next);
  }

  let body: ReactNode;
  if (!dto) {
    body = query.isError ? (
      <div className="grid gap-space-2">
        <p className="text-small text-status-error m-0">Could not load the deal structure.</p>
        <div>
          <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>Try again</Button>
        </div>
      </div>
    ) : (
      <p className="text-small text-gray-mid m-0">Loading the deal structure…</p>
    );
  } else if (step === 0) {
    body = (
      <StepBody>
        <p className="text-body text-onyx m-0" data-testid="deal-structure-wizard-intro">{W0.body}</p>
        <Actions>
          <Button
            data-testid="deal-structure-wizard-start"
            disabled={saving}
            onClick={() => void save({ step: 1 }, 1)}
          >
            {W0.start}
          </Button>
          {/* Saves nothing: a structure nobody started stays "Not built". */}
          <Button variant="secondary" disabled={saving} onClick={onClose}>
            {W0.notNow}
          </Button>
        </Actions>
      </StepBody>
    );
  } else if (step === 1) {
    const line = listingCashFlowLine(dto.suggested.listingCashFlow, dto.suggested.listingCashFlowType);
    const ready = cashFlowReady(type, amountText, estimateText);
    body = (
      <StepBody question={W1.question}>
        {line && (
          <p className="text-small text-gray-mid m-0" data-testid="deal-structure-wizard-listing-cash-flow">
            {line}
          </p>
        )}
        <CashFlowFields
          idPrefix="deal-structure-wizard"
          type={type}
          onType={setType}
          amountText={amountText}
          onAmountText={setAmountText}
          estimateText={estimateText}
          onEstimateText={setEstimateText}
        />
        {!type && <p className="text-caption text-gray-mid m-0">{W1.chooseType}</p>}
        <Actions>
          <NextButton
            disabled={saving || !ready}
            onClick={() =>
              void save(
                {
                  step: 2,
                  cashFlowType: type!,
                  cashFlow: parseMoneyText(amountText)!,
                  ...(type === "Sde" ? { freeCashFlowEstimate: parseMoneyText(estimateText)! } : {}),
                },
                2,
              )
            }
          />
        </Actions>
      </StepBody>
    );
  } else if (step === 2) {
    const s = dto.suggested;
    const down = parseMoneyText(downText, true);
    body = (
      <StepBody question={W2.question}>
        {s.downPayment != null && (
          <p className="text-small text-gray-mid m-0" data-testid="deal-structure-wizard-down-prefill">
            {downPaymentPrefillLine(
              s.downPayment,
              s.downPaymentSource,
              s.downPaymentSourceDate ? date(s.downPaymentSourceDate) : null,
            )}
          </p>
        )}
        <DownPaymentField idPrefix="deal-structure-wizard" text={downText} onText={setDownText} />
        <StaffNote>{W2.staffNote}</StaffNote>
        <Actions>
          <NextButton
            disabled={saving || down == null}
            onClick={() => void save({ step: 3, downPayment: down! }, 3)}
          />
        </Actions>
      </StepBody>
    );
  } else if (step === 3) {
    body = (
      <StepBody question={W3.question}>
        <PriceBasisCards
          idPrefix="deal-structure-wizard"
          basis={basis}
          onBasis={setBasis}
          customText={customText}
          onCustomText={setCustomText}
          figures={dto.figures}
        />
        <Actions>
          <NextButton
            disabled={saving || !priceReady(basis, customText)}
            onClick={() =>
              void save(
                {
                  step: 4,
                  priceBasis: basis!,
                  ...(basis === "Custom" ? { customPrice: parseMoneyText(customText)! } : {}),
                },
                4,
              )
            }
          />
        </Actions>
      </StepBody>
    );
  } else if (step === 4) {
    body = (
      <StepBody question={W4.heading}>
        <Review dto={dto} />
        <NoteField idPrefix="deal-structure-wizard" text={noteText} onText={setNoteText} />
        <Actions>
          <NextButton
            disabled={saving}
            onClick={() => void save({ step: 5, note: noteText.trim() }, 5)}
          />
        </Actions>
      </StepBody>
    );
  } else {
    body = (
      <StepBody>
        <label className="flex items-start gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            className="mt-space-1"
            data-testid="deal-structure-wizard-confirm"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
          <span>{W5.confirm}</span>
        </label>
        <Actions>
          <Button
            data-testid="deal-structure-wizard-save"
            disabled={saving || !confirmed}
            onClick={() => void save({ step: CONFIRM_STEP_INDEX, confirm: true }, "done")}
          >
            {W5.save}
          </Button>
          <Button variant="secondary" disabled={saving} onClick={() => setGoBackOpen((v) => !v)}>
            {W5.goBack}
          </Button>
        </Actions>
        {goBackOpen && (
          <div className="flex flex-wrap gap-space-2" data-testid="deal-structure-wizard-go-back">
            {WIZARD_STEPS.map((s, index) => ({ ...s, index }))
              .filter((s) => s.index > 0 && s.index < CONFIRM_STEP_INDEX)
              .map((s) => (
                <Button
                  key={s.key}
                  variant="secondary"
                  size="sm"
                  disabled={saving}
                  onClick={() => void save({ step: s.index }, s.index)}
                >
                  {s.label}
                </Button>
              ))}
          </div>
        )}
      </StepBody>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      size="lg"
      title={WIZARD_TITLE}
      footer={(
        <div className="flex items-center justify-between w-full gap-space-3">
          <Button variant="text" onClick={() => goTo(Math.max(step - 1, 0))} disabled={step === 0 || saving}>
            Back
          </Button>
          <span className="text-caption text-gray-mid" data-testid="deal-structure-wizard-counter">
            {`Step ${step + 1} of ${WIZARD_STEPS.length}`}
          </span>
        </div>
      )}
    >
      <div className="grid gap-space-4">
        <StatusStepper
          steps={WIZARD_STEPS.map((s) => ({ key: s.key, label: s.label }))}
          currentIndex={step}
          label="Deal structure progress"
          data-testid="deal-structure-wizard-stepper"
        />
        {body}
        {error && (
          <p role="alert" className="text-small text-status-error m-0" data-testid="deal-structure-wizard-error">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * W4 — the three headline figures (the 20-year schedule's), the two schedules
 * side by side with the staff-only Origination Coverage Ratio (no flag — it
 * cannot fall below the floor here, ruling Q11), the seller panel's pointer
 * (C11; no receipts figures, D7), all read from the server's figures, plus its
 * warnings verbatim.
 */
function Review({ dto }: { dto: BuyerDealStructureDto }) {
  const f = dto.figures;
  const issues = [...f.errors, ...f.warnings];
  const twenty = f.twentyYear ?? null;
  const ten = f.tenYear ?? null;
  const money = (v: number | null | undefined) => (v == null ? "" : formatCurrency(v));
  const balance = (v: number | null | undefined) => (v == null ? "" : v > 0 ? formatCurrency(v) : "—");
  const ratio = (v: number | null | undefined) => (v == null ? "—" : `${formatNumber(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`);
  const anyBalance = (twenty?.balance ?? 0) > 0 || (ten?.balance ?? 0) > 0;
  const rows: [string, string, string][] = [
    [W4.rows.maximum, money(twenty?.maximumFinanced), money(ten?.maximumFinanced)],
    [W4.rows.financed, money(twenty?.financed), money(ten?.financed)],
    ...(anyBalance ? [[W4.rows.balance, balance(twenty?.balance), balance(ten?.balance)] as [string, string, string]] : []),
    [W4.rows.financedMultiple, multipleOf(twenty?.financedMultiple), multipleOf(ten?.financedMultiple)],
    [W4.rows.coverage, ratio(twenty?.sizingCoverage), ratio(ten?.sizingCoverage)],
    [W4.rows.originationCoverage, ratio(twenty?.originationCoverage), ratio(ten?.originationCoverage)],
  ];
  return (
    <div className="grid gap-space-3" data-testid="deal-structure-wizard-review">
      <ResponsiveGrid cols={3} minColWidth="10rem" gap="sm">
        <Headline
          testId="deal-structure-review-price"
          label={STRUCTURE_CARD.rows.price}
          value={f.price}
          caption={dto.priceBasis ? PRICE_BASIS_LABELS[dto.priceBasis] : null}
        />
        <Headline
          testId="deal-structure-review-down"
          label={STRUCTURE_CARD.rows.downPayment}
          value={f.downPayment}
          caption={f.downPaymentFraction != null ? `${percentOf(f.downPaymentFraction)} of price · estimated` : null}
        />
        <Headline
          testId="deal-structure-review-financed"
          label={W4.amountFinanced}
          value={f.financed}
          caption={f.financedFraction != null ? `${percentOf(f.financedFraction)} ${W4.amountFinancedCaption}` : null}
        />
      </ResponsiveGrid>
      <div data-testid="deal-structure-review-financing">
        <SubTable<[string, string, string]>
          caption="The financing on each schedule"
          rows={rows}
          rowKey={(r) => r[0]}
          columns={[
            { key: "label", header: "", render: (r) => <span className="text-gray-mid">{r[0]}</span> },
            { key: "twenty", header: W4.scheduleHeads.twentyYear, align: "right", render: (r) => r[1] },
            { key: "ten", header: W4.scheduleHeads.tenYear, align: "right", render: (r) => r[2] },
          ]}
        />
      </div>
      <div className="grid gap-space-1">
        <h3 className="text-caption uppercase tracking-wide text-gray-mid m-0">{W4.rows.sellerHeading}</h3>
        <p className="text-small text-onyx m-0" data-testid="deal-structure-review-seller">{W4.sellerPointer}</p>
      </div>
      {issues.length > 0 && (
        <ul className="grid gap-space-1 m-0 pl-space-4" data-testid="deal-structure-review-warnings">
          {issues.map((w) => (
            <li key={`${w.code}:${w.message}`} className="text-small text-status-warning-ink">{w.message}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Headline({
  label,
  value,
  caption,
  testId,
}: {
  label: string;
  value: number | null | undefined;
  caption: string | null;
  testId: string;
}) {
  return (
    <div className="grid gap-space-1 content-start rounded-md bg-white-warm p-space-3 min-w-0" data-testid={testId}>
      <span className="text-caption uppercase tracking-wide text-gray-mid">{label}</span>
      <span className="text-lead font-semibold text-onyx">{value == null ? "—" : formatCurrency(value)}</span>
      {caption && <span className="text-caption text-gray-mid">{caption}</span>}
    </div>
  );
}

function StepBody({ question, children }: { question?: string; children: ReactNode }) {
  return (
    <div className="grid gap-space-3">
      {question && <h3 className="text-lead font-semibold text-onyx m-0">{question}</h3>}
      {children}
    </div>
  );
}

function Actions({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-space-2">{children}</div>;
}

function NextButton({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
  return (
    <Button data-testid="deal-structure-wizard-next" disabled={disabled} onClick={onClick}>
      Next
    </Button>
  );
}

function StaffNote({ children }: { children: ReactNode }) {
  return (
    <aside
      className="rounded-md border border-dashed border-gray-mid p-space-3 text-caption text-gray-mid"
      data-testid="deal-structure-wizard-staff-note"
    >
      {children}
    </aside>
  );
}

function clampStep(v: number): number {
  return Math.min(Math.max(0, Math.trunc(v)), CONFIRM_STEP_INDEX);
}

/** The Asking card is the default only when it can be chosen. */
function canUseAsking(dto: BuyerDealStructureDto): boolean {
  return dto.figures.askingPrice != null;
}
